import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { notifyCartUpdated } from '../utils/cartEvents';
import { useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../supabaseClient';
import UserLayout from './user/UserLayout';
import { QrCode, X, ShoppingBag, Camera, ArrowLeft, Package, AlertCircle } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { showNotification, showError } from '../utils/notificationHandler';
import { useOrderingEnabled } from '../hooks/useOrderingEnabled';
import { logCustomerSearch, buildShownSnapshot } from '../utils/searchLogger';
import { DotLoader } from 'react-spinners';

const Scanner = () => {
  const { enabled: orderingEnabled, message: orderingMessage } = useOrderingEnabled();
  const [isScanning, setIsScanning] = useState(false);
  const [html5QrCode, setHtml5QrCode] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [account, setAccount] = useState(null);
  const [isProcessingScan, setIsProcessingScan] = useState(false);
  const [scannedItem, setScannedItem] = useState(null);
  const history = useHistory();

  const placeholderImageUrl =
    'https://syd1.digitaloceanspaces.com/cdn-assets.mhs/images/placeholders/mhs_placeholder.png';

  // Fetch account ID on component mount
  useEffect(() => {
    const fetchAccountId = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('accounts')
          .select('id, is_admin, is_staff, company_name, full_name')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching account ID:', error);
        } else if (data) {
          setAccountId(data.id);
          setAccount(data);
        }
      }
    };

    fetchAccountId();
  }, []);

  const scannerConfig = useMemo(() => ({ fps: 10, qrbox: { width: 250, height: 250 } }), []);

  const onScanSuccess = useCallback(
    async (decodedText) => {
      if (isProcessingScan) {
        return;
      }
      setIsProcessingScan(true);

      console.log('Scan successful:', decodedText);

      try {
        if (!accountId) {
          showNotification('Account ID not available', 'error');
          return;
        }

        const response = await fetch('/api/enquire/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            partNumber: decodedText,
            account_id: accountId,
          }),
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Item not found');
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        let itemDetails = {};

        if (data.cross_references && data.cross_references.length > 0) {
          // Alternative part number
          const primaryPart = data.cross_references[0];
          itemDetails = {
            id: Date.now(),
            PartNumber: decodedText,
            PrimaryPartNumber: primaryPart.prod_code,
            Description: primaryPart.main_description,
            Quantity: 1,
            Price: primaryPart.buy_price || primaryPart.base_price || null,
            QuantityAvailable: primaryPart.qty_available || 0,
            SmallImageUrl: primaryPart.small_image_url || placeholderImageUrl,
            isAlternative: true,
            isFound: true,
          };
        } else if (data.prod_code || data.bom_code) {
          // Primary part number
          itemDetails = {
            id: Date.now(),
            PartNumber: decodedText,
            Description: data.Description || 'N/A',
            Quantity: 1,
            Price: data.buy_price || data.base_price || null,
            QuantityAvailable: data.qty_available || 0,
            SmallImageUrl: data.small_image_url || placeholderImageUrl,
            isAlternative: false,
            isFound: true,
          };
        } else {
          throw new Error('Part number not found');
        }

        // Log the scan with the price/stock shown in the confirmation modal
        const shownItem = itemDetails.isAlternative ? data.cross_references[0] : data;
        logCustomerSearch({
          userId: accountId,
          account,
          searchTerm: decodedText,
          searchType: 'exact',
          success: true,
          resultsCount: 1,
          partFound: itemDetails.isAlternative ? itemDetails.PrimaryPartNumber : (data.prod_code || data.bom_code),
          source: 'scanner',
          shown: buildShownSnapshot(shownItem, false),
        });

        // Show confirmation modal
        setScannedItem(itemDetails);
      } catch (error) {
        console.error(`Error fetching details for ${decodedText}:`, error);
        logCustomerSearch({
          userId: accountId,
          account,
          searchTerm: decodedText,
          searchType: 'exact',
          success: false,
          source: 'scanner',
        });
        showError(error, 'Failed to fetch item details. Please try again.');
      } finally {
        setIsProcessingScan(false);
      }
    },
    [accountId, account, isProcessingScan]
  );

  const onScanFailure = useCallback((error) => {
    console.warn('Scan failed:', error);
  }, []);

  // Initialize or clear scanner based on isScanning state and accountId
  useEffect(() => {
    let qrCodeScanner;

    if (isScanning && accountId) {
      qrCodeScanner = new Html5Qrcode('reader');
      setHtml5QrCode(qrCodeScanner);

      qrCodeScanner
        .start({ facingMode: 'environment' }, scannerConfig, onScanSuccess, onScanFailure)
        .catch((err) => {
          console.error('Error starting scanner:', err);
          if (err.name === 'NotAllowedError') {
            showError(err, 'Camera access denied. Please allow camera permissions.');
          } else if (err.name === 'NotFoundError') {
            showError(err, 'No camera found. Please connect a camera and try again.');
          } else {
            showError(err, 'Failed to start scanner. Please try again.');
          }
          setIsScanning(false);
        });
    }

    // Cleanup function
    return () => {
      if (qrCodeScanner) {
        qrCodeScanner
          .stop()
          .then(() => {
            qrCodeScanner.clear();
            setHtml5QrCode(null);
          })
          .catch((err) => {
            console.error('Failed to stop scanner during cleanup:', err);
          });
      }
    };
  }, [isScanning, accountId, scannerConfig, onScanSuccess, onScanFailure]);

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      showNotification('Successfully signed out!', 'success');
    } catch (error) {
      showError(error, 'Failed to sign out. Please try again.');
    }
  };

  const startScanner = () => {
    console.log('startScanner called');
    setIsScanning(true);
  };

  const stopScanner = () => {
    console.log('stopScanner called');
    setIsScanning(false);
  };

  const addToCartDirectly = (item) => {
    if (!orderingEnabled) {
      showNotification(orderingMessage, 'error');
      return false;
    }
    const cart = JSON.parse(localStorage.getItem('cart')) || [];
    const existingItemIndex = cart.findIndex(
      (cartItem) => cartItem.partNumber === item.PartNumber
    );
    if (existingItemIndex >= 0) {
      cart[existingItemIndex].quantity += item.Quantity;
    } else {
      cart.push({
        partNumber: item.PartNumber,
        description: item.Description,
        quantity: item.Quantity,
        price: item.Price,
      });
    }
    localStorage.setItem('cart', JSON.stringify(cart));
    notifyCartUpdated();
  };

  const proceedToCheckout = () => {
    history.push('/checkout');
  };

  const ConfirmationModal = ({ item, onConfirm, onCancel }) => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-lg shadow-xl w-full max-w-md"
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {orderingEnabled ? 'Confirm Add to Cart' : 'Scanned Item'}
              </h2>
              <button
                onClick={onCancel}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Product Image */}
              <div className="flex justify-center">
                <img 
                  src={item.SmallImageUrl} 
                  alt={item.Description} 
                  className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                />
              </div>
              
              {/* Product Details */}
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Part Number:</span>
                  <span className="text-sm text-gray-900 font-mono">{item.PartNumber}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Description:</span>
                  <span className="text-sm text-gray-900 text-right flex-1 ml-2">{item.Description}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Price:</span>
                  <span className="text-sm text-gray-900 font-semibold">
                    {item.Price ? `$${item.Price.toFixed(2)}` : 'Quote Required'}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Available:</span>
                  <span className={`text-sm font-medium ${item.QuantityAvailable > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {item.QuantityAvailable > 0 ? `${item.QuantityAvailable} in stock` : 'Out of stock'}
                  </span>
                </div>
                
                {item.isAlternative && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <AlertCircle className="w-4 h-4 text-blue-600 mr-2" />
                      <span className="text-sm text-blue-800">
                        Alternative part found: {item.PrimaryPartNumber}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex space-x-3 mt-6">
              <button
                onClick={onCancel}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
              >
                {orderingEnabled ? 'Cancel' : 'Close'}
              </button>
              {orderingEnabled && (
                <button
                  onClick={onConfirm}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  Add to Cart
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              QR Code <span className="text-brand">Scanner</span>
            </h1>
            <p className="text-gray-600 mt-1">
              Scan QR codes to quickly find and add parts to your cart
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <button
              onClick={() => history.push('/user/dashboard')}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </button>
          </div>
        </motion.div>

        {/* Scanner Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="space-y-6">
            {/* Scanner Status */}
            <div className="flex items-center justify-center space-x-2">
              <Camera className="w-5 h-5 text-gray-600" />
              <span className="text-sm text-gray-600">
                {isScanning ? 'Camera is active' : 'Camera is ready'}
              </span>
              <div className={`w-2 h-2 rounded-full ${isScanning ? 'bg-green-500' : 'bg-gray-400'}`}></div>
            </div>

            {/* Scanner Interface */}
            {isScanning ? (
              <div className="space-y-4">
                <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <div id="reader" className="w-full"></div>
                </div>
                <div className="flex justify-center">
                  <button
                    onClick={stopScanner}
                    className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors duration-200"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Stop Scanning
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                    <QrCode className="w-12 h-12 text-gray-400" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Scan</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Position the QR code within the camera frame to scan part numbers
                  </p>
                  <button
                    onClick={startScanner}
                    className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
                  >
                    <QrCode className="w-4 h-4 mr-2" />
                    Start Scanning
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <ShoppingBag className="w-5 h-5 mr-2 text-gray-600" />
            Quick Actions
          </h3>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => history.push('/user/cart')}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              View Cart
            </button>
            <button
              onClick={proceedToCheckout}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-hover focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-colors duration-200"
            >
              <Package className="w-4 h-4 mr-2" />
              Proceed to Checkout
            </button>
          </div>
        </motion.div>
      </div>

      {/* Loading Overlay */}
      {isProcessingScan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="text-center">
            <DotLoader color="#3b82f6" size={60} />
            <p className="text-white mt-4 text-lg">Processing scan...</p>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {scannedItem && (
        <ConfirmationModal
          item={scannedItem}
          onConfirm={() => {
            const added = addToCartDirectly(scannedItem);
            setScannedItem(null);
            if (added !== false) {
              showNotification('Item added to cart successfully!', 'success');
            }
          }}
          onCancel={() => {
            setScannedItem(null);
            if (orderingEnabled) {
              showNotification('Item not added to cart.', 'info');
            }
          }}
        />
      )}
    </UserLayout>
  );
};

export default Scanner;
